import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { LabelPrinterConfirmHost } from "@/labels/print/LabelPrinterConfirm";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { usePhaseTimesAvailable } from "@/hooks/useWorkflowDefinitions";

function PhaseTimesRoute({ children }: { children: JSX.Element }) {
  const { available, isLoading } = usePhaseTimesAvailable();
  if (isLoading) return null;
  return available ? children : <Navigate to="/settings/data" replace />;
}

function LegacyStorageLabelsRedirect() {
  const { locationId } = useParams();
  return <Navigate to={`/settings/storage/${locationId ?? ""}/labels`} replace />;
}

function AdminHomeRedirect() {
  const { isSuperadmin, isLoading: saLoading } = useIsSuperadmin();
  const { allowed, isLoading: caLoading } = useCanManageCompany();
  if (saLoading || caLoading) return null;
  if (isSuperadmin) return <Navigate to="/platform/companies" replace />;
  return <Navigate to={allowed ? "/settings/company" : "/settings/profile"} replace />;
}

function LegacyDesignerRedirect() {
  const { key } = useParams();
  return <Navigate to={`/platform/workflows/${key ?? ""}/designer`} replace />;
}
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import CompanyAdminRoute from "./components/CompanyAdminRoute";
import SuperadminRoute from "./components/SuperadminRoute";
import { AppLayout } from "./components/AppLayout";
import { SettingsLayout } from "./components/settings/SettingsLayout";
import { PlatformLayout } from "./components/settings/PlatformLayout";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Index from "./pages/Index";
import ApplianceDetail from "./pages/ApplianceDetail";
import RepairWorkflow from "./pages/RepairWorkflow";
import RepairProcess from "./pages/RepairProcess";
import RefurbishmentTriage from "./pages/RefurbishmentTriage";
import RefurbishmentResults from "./pages/RefurbishmentResults";
import RefurbishmentStart from "./pages/RefurbishmentStart";
import RefurbishmentProcess from "./pages/RefurbishmentProcess";
import RefurbishmentPassport from "./pages/RefurbishmentPassport";
import PrintPassportLabel from "./pages/PrintPassportLabel";
import HarvestProcess from "./pages/HarvestProcess";
import WorkOrders from "./pages/WorkOrders";
import Inventory from "./pages/Inventory";
import Store from "./pages/Store";
import Profile from "./pages/Profile";
import PartsHarvestingSettings from "./pages/PartsHarvestingSettings";
import WorkflowManagement from "./pages/WorkflowManagement";
import UserRoleManagement from "./pages/UserRoleManagement";
import PriceDashboard from "./pages/PriceDashboard";
import AppliancePriceTracker from "./pages/AppliancePriceTracker";
import DataManagement from "./pages/DataManagement";
import BrandHarvestDefaults from "./pages/admin/BrandHarvestDefaults";
import LegacyParts from "./pages/admin/LegacyParts";
import GradePricingDefaults from "./pages/admin/GradePricingDefaults";
import CompanyDataManagement from "./pages/CompanyDataManagement";
import RepairGuideCenter from "./pages/RepairGuideCenter";
import BrandsManagement from "./pages/BrandsManagement";
import DeviceRules from "./pages/DeviceRules";
import DeviceScanner from "./pages/DeviceScanner";
import StorageManagement from "./pages/admin/StorageManagement";
import StorageLabelsPrint from "./pages/admin/StorageLabelsPrint";
import PhaseTimeAnalytics from "./pages/admin/PhaseTimeAnalytics";
import SparePartLabels from "./pages/SparePartLabels";
import SparePartConfiguration from "./pages/admin/SparePartConfiguration";
import BrandDecoding from "./pages/admin/BrandDecoding";
import DecoderCoverage from "./pages/admin/DecoderCoverage";
import CompanySettings from "./pages/admin/CompanySettings";
import AdminLanguages from "./pages/admin/Languages";
import WorkflowRunner from "./pages/WorkflowRunner";
import WorkflowDesigner from "./pages/admin/WorkflowDesigner";
import PlatformUsers from "./pages/admin/PlatformUsers";
import RecommendationEngineAdmin from "./pages/admin/RecommendationEngineAdmin";
import ApplianceCategoryManagement from "./pages/admin/ApplianceCategoryManagement";
import AuditLog from "./pages/admin/AuditLog";
import CompaniesAdmin from "./pages/admin/CompaniesAdmin";
import CompanyDetail from "./pages/admin/CompanyDetail";
import { CompanyUsersSection, CompanyFeaturesSection, CompanyWorkflowsSection } from "./pages/admin/CompanySection";
import { LegacyCompanyRedirect } from "./pages/admin/LegacyCompanyRedirect";
import BatchImport from "./pages/admin/BatchImport";
import SparePartDetail from "./pages/SparePartDetail";
import PartTypeDetail from "./pages/PartTypeDetail";
import BinDetail from "./pages/BinDetail";
import NotFound from "./pages/NotFound";
// New unified settings pages
import Preferences from "./pages/settings/Preferences";
import SettingsProfile from "./pages/settings/Profile";
import Security from "./pages/settings/Security";
import LabelPrinting from "./pages/settings/LabelPrinting";

const queryClient = new QueryClient();

const ProtectedOutlet = () => (
  <ProtectedRoute>
    <AppLayout>
      <Outlet />
    </AppLayout>
  </ProtectedRoute>
);

const SettingsOutlet = () => (
  <ProtectedRoute>
    <AppLayout bare>
      <SettingsLayout>
        <Outlet />
      </SettingsLayout>
    </AppLayout>
  </ProtectedRoute>
);

const PlatformOutlet = () => (
  <ProtectedRoute>
    <AppLayout bare>
      <SuperadminRoute>
        <PlatformLayout>
          <Outlet />
        </PlatformLayout>
      </SuperadminRoute>
    </AppLayout>
  </ProtectedRoute>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <LabelPrinterConfirmHost />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Password recovery landing page (reached from the emailed link) */}
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Public route - Digital Product Passport accessible without login */}
            <Route path="/passport/:id" element={<RefurbishmentPassport />} />
            <Route path="/passport/:id/label" element={<PrintPassportLabel />} />

            <Route element={<ProtectedOutlet />}>
              <Route path="/" element={<Navigate to="/work-orders" replace />} />
              <Route path="/search" element={<Index />} />
              <Route path="/batch-import" element={<BatchImport />} />
              <Route path="/work-orders" element={<WorkOrders />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/inventory/parts/:partTypeId" element={<PartTypeDetail />} />
              <Route path="/spare-part/:token" element={<SparePartDetail />} />
              <Route path="/bin/:id" element={<BinDetail />} />
              <Route path="/store" element={<Store />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/scanner" element={<DeviceScanner />} />
              <Route path="/appliance/:id" element={<ApplianceDetail />} />
              <Route path="/appliance/:id/repair" element={<RepairWorkflow />} />
              <Route path="/appliance/:id/repair/process" element={<RepairProcess />} />
              <Route path="/appliance/:id/refurbish" element={<RefurbishmentTriage />} />
              <Route path="/appliance/:id/refurbish/results" element={<RefurbishmentResults />} />
              <Route path="/appliance/:id/refurbish/start" element={<RefurbishmentStart />} />
              <Route path="/appliance/:id/refurbish/process" element={<RefurbishmentProcess />} />
              <Route path="/appliance/:id/refurbish/passport" element={<RefurbishmentPassport />} />
              <Route path="/appliance/:id/harvest" element={<HarvestProcess />} />
              <Route path="/appliance/:id/harvest/labels" element={<SparePartLabels />} />
              <Route path="/appliance/:id/workflow/:workflowKey" element={<WorkflowRunner />} />
            </Route>

            {/* Unified Settings Area */}
            <Route element={<SettingsOutlet />}>
              <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
              <Route path="/settings/preferences" element={<Preferences />} />
              <Route path="/settings/profile" element={<SettingsProfile />} />
              <Route path="/settings/notifications" element={<Navigate to="/settings/preferences" replace />} />
              <Route path="/settings/security" element={<Security />} />
              <Route path="/settings/language" element={<Navigate to="/settings/preferences" replace />} />
              <Route path="/settings/label-printing" element={<LabelPrinting />} />
              {/* Company settings (admin-only) */}
              <Route path="/settings/company" element={<CompanyAdminRoute><CompanySettings /></CompanyAdminRoute>} />
              <Route path="/settings/company/languages" element={<CompanyAdminRoute><AdminLanguages /></CompanyAdminRoute>} />
              <Route path="/settings/company/grade-pricing" element={<Navigate to="/settings/parts-harvesting#grade-pricing" replace />} />
              <Route path="/settings/users" element={<CompanyAdminRoute><UserRoleManagement /></CompanyAdminRoute>} />
              <Route path="/settings/parts-harvesting" element={<CompanyAdminRoute><PartsHarvestingSettings /></CompanyAdminRoute>} />
              <Route path="/settings/triage-rules" element={<CompanyAdminRoute><DeviceRules /></CompanyAdminRoute>} />
              <Route path="/settings/storage" element={<CompanyAdminRoute><StorageManagement /></CompanyAdminRoute>} />
              <Route path="/settings/storage/:locationId/labels" element={<CompanyAdminRoute><StorageLabelsPrint /></CompanyAdminRoute>} />
              <Route path="/settings/data" element={<CompanyAdminRoute><CompanyDataManagement /></CompanyAdminRoute>} />
              <Route path="/settings/data/phase-times" element={<CompanyAdminRoute><PhaseTimesRoute><PhaseTimeAnalytics /></PhaseTimesRoute></CompanyAdminRoute>} />
              <Route path="/settings/data/repair-guides" element={<CompanyAdminRoute><RepairGuideCenter /></CompanyAdminRoute>} />
              <Route path="/settings/data/manuals" element={<Navigate to="/settings/data/repair-guides" replace />} />

            </Route>

            {/* Platform Settings (superadmin-only) */}
            <Route element={<PlatformOutlet />}>
              <Route path="/platform" element={<Navigate to="/platform/companies" replace />} />
              <Route path="/platform/companies/users" element={<LegacyCompanyRedirect section="users" />} />
              <Route path="/platform/companies" element={<CompaniesAdmin />} />
              <Route path="/platform/companies/:companyId" element={<CompanyDetail />}>
                <Route path="users" element={<CompanyUsersSection />} />
                <Route path="features" element={<CompanyFeaturesSection />} />
                <Route path="workflows" element={<CompanyWorkflowsSection />} />
              </Route>
              
              <Route path="/platform/workflows" element={<WorkflowManagement />} />
              <Route path="/platform/workflow-definitions" element={<LegacyCompanyRedirect section="workflows" />} />
              <Route path="/platform/workflows/:key/designer" element={<WorkflowDesigner />} />
              <Route path="/platform/navigation" element={<LegacyCompanyRedirect section="features" />} />
              <Route path="/platform/audit-log" element={<AuditLog />} />
              <Route path="/platform/users" element={<PlatformUsers />} />
              <Route path="/platform/superadmin" element={<Navigate to="/platform/users" replace />} />

              <Route path="/platform/data" element={<DataManagement />} />
              <Route path="/platform/data/appliance-categories" element={<ApplianceCategoryManagement />} />
              <Route path="/platform/data/recommendation-engine" element={<RecommendationEngineAdmin />} />
              <Route path="/platform/data/spare-parts" element={<SparePartConfiguration />} />
              <Route path="/platform/data/brand-harvest-defaults" element={<BrandHarvestDefaults />} />
              <Route path="/platform/data/legacy-parts" element={<LegacyParts />} />
              <Route path="/platform/data/grade-pricing" element={<GradePricingDefaults />} />
              <Route path="/platform/data/appliance-prices" element={<AppliancePriceTracker />} />
              <Route path="/platform/data/refurbished-prices" element={<PriceDashboard />} />
              <Route path="/platform/brands" element={<BrandsManagement />} />
              <Route path="/platform/brands/decoding" element={<BrandDecoding />} />
              <Route path="/platform/brands/decoding-coverage" element={<DecoderCoverage />} />
              <Route path="/platform/batch-import" element={<Navigate to="/batch-import" replace />} />
            </Route>

            {/* Legacy admin redirects */}
            <Route path="/admin" element={<AdminHomeRedirect />} />
            <Route path="/admin/workflows" element={<Navigate to="/platform/workflows" replace />} />
            <Route path="/admin/data" element={<Navigate to="/platform/data" replace />} />
            <Route path="/admin/repair-guides" element={<Navigate to="/settings/data/repair-guides" replace />} />
            <Route path="/admin/manuals" element={<Navigate to="/settings/data/repair-guides" replace />} />
            <Route path="/admin/brands" element={<Navigate to="/platform/brands" replace />} />
            <Route path="/admin/users" element={<Navigate to="/settings/users" replace />} />
            <Route path="/admin/company" element={<Navigate to="/platform/companies" replace />} />
            <Route path="/admin/device-rules" element={<Navigate to="/settings/triage-rules" replace />} />
            <Route path="/admin/appliance-price-tracker" element={<Navigate to="/platform/data/appliance-prices" replace />} />
            <Route path="/settings/data/appliance-prices" element={<Navigate to="/platform/data/appliance-prices" replace />} />
            <Route path="/price-dashboard" element={<Navigate to="/platform/data/refurbished-prices" replace />} />
            <Route path="/admin/harvesting" element={<Navigate to="/settings/parts-harvesting" replace />} />
            <Route path="/admin/recommendation-engine" element={<Navigate to="/platform/data/recommendation-engine" replace />} />
            <Route path="/admin/storage" element={<Navigate to="/settings/storage" replace />} />
            <Route path="/admin/storage/:locationId/labels" element={<LegacyStorageLabelsRedirect />} />
            <Route path="/admin/phase-times" element={<Navigate to="/settings/data/phase-times" replace />} />
            <Route path="/admin/spare-parts" element={<Navigate to="/platform/data/spare-parts" replace />} />
            <Route path="/admin/grade-pricing" element={<Navigate to="/settings/parts-harvesting#grade-pricing" replace />} />
            <Route path="/admin/decoding" element={<Navigate to="/platform/brands/decoding" replace />} />
            <Route path="/admin/decoding-coverage" element={<Navigate to="/platform/brands/decoding-coverage" replace />} />
            <Route path="/admin/company-settings" element={<Navigate to="/settings/company" replace />} />
            <Route path="/admin/languages" element={<Navigate to="/settings/company/languages" replace />} />
            <Route path="/admin/workflow-definitions" element={<Navigate to="/platform/companies" replace />} />
            <Route path="/admin/workflows/:key/designer" element={<LegacyDesignerRedirect />} />
            <Route path="/admin/navigation" element={<Navigate to="/platform/companies" replace />} />
            <Route path="/admin/superadmin" element={<Navigate to="/platform/users" replace />} />
            <Route path="/admin/appliance-categories" element={<Navigate to="/platform/data/appliance-categories" replace />} />
            <Route path="/admin/audit-log" element={<Navigate to="/platform/audit-log" replace />} />
            <Route path="/admin/companies" element={<Navigate to="/platform/companies" replace />} />
            <Route path="/admin/batch-import" element={<Navigate to="/batch-import" replace />} />
            <Route path="/company-admin" element={<Navigate to="/settings/company" replace />} />
            <Route path="/company-admin/parts-harvesting" element={<Navigate to="/settings/parts-harvesting" replace />} />
            <Route path="/company-admin/data" element={<Navigate to="/settings/data" replace />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
